import {
  HTMLAttributes,
  ReactNode,
  createContext,
  forwardRef,
  memo,
  useContext,
  useMemo
} from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Label as UIKitLabel } from "shared/ui/label/Label"
import { Skeleton } from "shared/ui/skeleton/Skeleton"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { FormContext } from "../form/FormContext"
import "./styles/_readonly.scss"

export interface ReadonlyContextType {
  isLoading?: boolean
}

export const ReadonlyContext = createContext<ReadonlyContextType>({
  isLoading: false
})

export interface ReadonlyRootProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children: ReactNode
  isLoading?: boolean
}

const Root = memo(
  forwardRef<HTMLDivElement, ReadonlyRootProps>(
    ({ children, className, isLoading, ...props }, ref) => {
      const contextValue = useMemo(
        () => ({
          isLoading
        }),
        [isLoading]
      )

      return (
        <ReadonlyContext.Provider value={contextValue}>
          <div
            {...props}
            ref={ref}
            className={classNames(
              "readonly",
              { "readonly--loading": isLoading },
              className
            )}
          >
            {children}
          </div>
        </ReadonlyContext.Provider>
      )
    }
  )
)
Root.displayName = "ReadonlyRoot"

export interface ReadonlyLabelProps {
  children?: ReactNode
  className?: string
  icon?: IconName
  isLoading?: boolean
}

const Label = memo(
  ({ children, className, icon, isLoading }: ReadonlyLabelProps) => {
    const { isReadonly } = useContext(FormContext)

    if (!children && !isLoading) {
      return null
    }

    return (
      <UIKitLabel
        className={classNames("readonly__label", className)}
        icon={icon}
        variant={isReadonly === false ? "bold" : "subtle"}
        isLoading={isLoading}
      >
        {children}
      </UIKitLabel>
    )
  }
)
Label.displayName = "ReadonlyLabel"

export interface ReadonlyValueProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: ReactNode
  /**
   * When true, prevents the locked variant from being shown even inside an editable form.
   */
  disableLocked?: boolean
  flex?: boolean
  isLoading?: boolean
  /**
   * Tooltip text for the info icon in locked variant.
   * Defaults to a translated message about auto-filled fields.
   */
  tooltip?: string
}

const Value = memo(
  forwardRef<HTMLDivElement, ReadonlyValueProps>(
    (
      {
        children,
        className,
        disableLocked,
        flex,
        isLoading: valueLoading,
        tooltip,
        ...props
      },
      ref
    ) => {
      const { isLoading: contextLoading } = useContext(ReadonlyContext)
      const { isReadonly } = useContext(FormContext)
      const { t } = useTranslation()
      const isLoading = valueLoading ?? contextLoading

      const isInEditableForm = isReadonly === false && !disableLocked
      const shouldShowLockedVariant = isInEditableForm && !isLoading
      const shouldShowLockedLoading = isInEditableForm && isLoading

      // Show locked variant loading (larger loader in locked field style)
      if (shouldShowLockedLoading) {
        return (
          <div
            {...props}
            ref={ref}
            className={classNames(
              "readonly__value",
              "readonly__value--locked",
              "readonly__value--loading",
              className
            )}
          >
            <Skeleton flex />
          </div>
        )
      }

      if (shouldShowLockedVariant) {
        return (
          <div
            {...props}
            ref={ref}
            className={classNames(
              "readonly__value",
              "readonly__value--locked",
              className
            )}
          >
            <span className="readonly__locked-content">{children}</span>
            <div className="readonly__locked-icons">
              <Icon name={IconName.EDIT_OFF} size={IconSize.SMALL} />
              <Tooltip
                content={
                  (tooltip || t("Readonly.LockedFieldTooltip")) as ReactNode
                }
                placement="top"
              >
                <span>
                  <Icon name={IconName.INFO} size={IconSize.SMALL} />
                </span>
              </Tooltip>
            </div>
          </div>
        )
      }

      return (
        <div
          {...props}
          ref={ref}
          className={classNames(
            "readonly__value",
            { "readonly__value--loading": isLoading },
            className
          )}
        >
          {isLoading ? <Skeleton flex={flex} /> : children}
        </div>
      )
    }
  )
)
Value.displayName = "ReadonlyValue"

export const Readonly = {
  Root,
  Label,
  Value
}
